import { useState, useCallback, useEffect, useRef } from 'react';
import { useProcessing } from '../context';
import { useProcessingSocket } from './useProcessingSocket';
import { ProcessingType } from '../types';
import { Message } from '../../../api/websocket/types';
import { TaskResponse } from '../../../features/template/types';
import { createActionsFromMessage } from '../../processing/utils';
import { useWebSocket } from '../../../api/websocket/WebSocketContext';

interface UseProcessingOperationOptions {
  processingType: ProcessingType;
  onComplete?: () => void;
  onMessage?: (message: Message) => void;
  onStageComplete?: (stageName: string, result: Record<string, unknown>) => void;
}

interface StartProcessingParams {
  taskResponse: TaskResponse;
}

export function useProcessingOperation({
  processingType,
  onComplete,
  onMessage,
  onStageComplete,
}: UseProcessingOperationOptions) {
  const { dispatch, state } = useProcessing();
  const [taskId, setTaskId] = useState<string | null>(null);
  const [wsUrl, setWsUrl] = useState<string>('');
  const [error, setError] = useState<Error | null>(null);
  const isProcessingRef = useRef(false);
  const { instance: wsManager } = useWebSocket();
  const stateRef = useRef(state);

  // Keep the ref updated with the latest state
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // Default onMessage handler to transform WebSocket messages to actions
  const defaultOnMessageHandler = useCallback((message: Message) => {
    // Use the current state from the ref
    const actions = createActionsFromMessage(message, processingType, stateRef.current);
    actions.forEach((action) => dispatch(action));
  }, [processingType, dispatch]);

  // Use provided onMessage, or fallback to the default handler
  const effectiveOnMessage = onMessage || defaultOnMessageHandler;

  // Debug logging
  const debugLog = (message: string, extra: Record<string, unknown> = {}) => {
    console.log(`[useProcessingOperation] ${message}`, {
      taskId,
      wsUrl,
      error,
      isProcessing: isProcessingRef.current,
      ...extra,
      timestamp: new Date().toISOString(),
    });
  };

  // Handle connection failures
  const handleConnectionFailure = useCallback(() => {
    debugLog('Connection failure handler called');
    dispatch({ type: 'SET_CONNECTION_STATUS', payload: { status: 'disconnected' } });
  }, [dispatch]);

  // Handle successful connection
  const handleConnectionSuccess = useCallback(() => {
    debugLog('Connection success handler called');
    dispatch({ type: 'SET_CONNECTION_STATUS', payload: { status: 'connected' } });
  }, [dispatch]);

  // Use the hook version of ProcessingSocket with all handlers
  const { state: socketState } = useProcessingSocket({
    taskId: taskId || '',
    onMessage: effectiveOnMessage,
    onComplete: useCallback(() => {
      debugLog('Completion handler called', { currentState: stateRef.current });
      isProcessingRef.current = false;
      
      // Ensure we have the latest state before calling onComplete
      setTimeout(() => {
        debugLog('Calling onComplete with final state', { finalState: stateRef.current });
        onComplete?.();
      }, 0);
    }, [onComplete]),
    onStageComplete,
    onConnectionFailure: handleConnectionFailure,
    onConnectionSuccess: handleConnectionSuccess,
  });

  // Keep connection status in sync with socket state
  useEffect(() => {
    debugLog('Socket state changed', { 
      newStatus: socketState.status,
      isProcessing: isProcessingRef.current
    });

    // Only update state if we're still processing
    if (!isProcessingRef.current) {
      debugLog('Ignoring socket state change - not processing');
      return;
    }

    // Handle terminal states
    if (socketState.status === 'completed') {
      debugLog('Processing completed');
      isProcessingRef.current = false;
      setTaskId(null);
      setWsUrl('');
      dispatch({ type: 'SET_CONNECTION_STATUS', payload: { status: 'disconnected' } });
    } else if (socketState.status === 'error') {
      debugLog('Socket error occurred', { error: socketState.error });
      setError(new Error(socketState.error));
      setTaskId(null);
      setWsUrl('');
      isProcessingRef.current = false;
      dispatch({ type: 'SET_CONNECTION_STATUS', payload: { status: 'error' } });

    } else {
      // For non-terminal states, just update the connection status
      const status = socketState.status === 'active' ? 'connected' : 
                    (socketState.status === 'connecting' || socketState.status === 'idle') ? 'connecting' : 'disconnected';
      dispatch({ type: 'SET_CONNECTION_STATUS', payload: { status } });
    }
  }, [socketState, dispatch]);

  const reset = useCallback(() => {
    debugLog('Reset called');
    setError(null);
    setTaskId(null);
    setWsUrl('');
    isProcessingRef.current = false;
    dispatch({ type: 'SET_CONNECTION_STATUS', payload: { status: 'disconnected' } });
  }, [dispatch]);

  const startProcessing = useCallback(async ({ taskResponse }: StartProcessingParams) => {
    debugLog('Starting processing', { taskResponse });
    
    // Reset any previous error state
    setError(null);
    isProcessingRef.current = true;

    try {
      if (taskResponse && taskResponse.connection?.uri) {
        wsManager?.setUrl(taskResponse.connection.uri);
      }

      setTaskId(taskResponse.task_id);
      setWsUrl(taskResponse.connection.uri);
      dispatch({
        type: 'START_PROCESSING',
        payload: { processingType },
      });

    } catch (err) {
      debugLog('Processing setup failed', { error: err });
      
      // Create user-friendly error message
      let errorMessage = 'Failed to start processing';
      if (err instanceof Error) {
        if ('code' in err && err.code === 'ERR_NETWORK') {
          errorMessage = 'Network error - please check your connection and try again';
        } else {
          errorMessage = err.message || errorMessage;
        }
      }
      
      const errorObj = new Error(errorMessage);
      
      // Only update error state if we're still processing
      if (!isProcessingRef.current) {
        debugLog('Processing cancelled during error handling');
        return;
      }

      setError(errorObj);
      setTaskId(null);
      setWsUrl('');
      isProcessingRef.current = false;
      dispatch({ type: 'SET_CONNECTION_STATUS', payload: { status: 'error' } });
      
      throw errorObj;
    }
  }, [debugLog, dispatch, processingType, wsManager]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      debugLog('Unmounting');
      // Removed the automatic reset on unmount to preserve final state for testing
      // if (!isProcessingRef.current) {
      //   reset();
      // }
    };
  }, [reset]);

  return {
    startProcessing,
    reset,
    isLoading: socketState.status === 'connecting' || socketState.status === 'active',
    error: error || (socketState.status === 'error' ? new Error(socketState.error) : null),
    taskId: taskId || '',
    wsUrl,
  };
} 